import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { listParticipantDocuments, archiveParticipantDocument } from './participantDocumentsApi';
import { ParticipantDocumentList } from './ParticipantDocumentList';
import { ParticipantDocumentUploadModal } from './ParticipantDocumentUploadModal';
import { ParticipantDocumentEmptyState } from './ParticipantDocumentEmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Search, RefreshCw } from 'lucide-react';

const CATEGORIES = [
  { value: 'ALL', label: 'All Types' },
  { value: 'ID', label: 'ID' },
  { value: 'SERVICE_AGREEMENT', label: 'Service Agreement' },
  { value: 'CONSENT', label: 'Consent' },
  { value: 'POLICY', label: 'Policy' },
  { value: 'HR', label: 'HR' },
  { value: 'INCIDENT_DOC', label: 'Incident' },
  // Phase 2B (Feb 2026) — onboarding categories
  { value: 'NDIS_PLAN', label: 'NDIS Plan' },
  { value: 'BEHAVIOUR_SUPPORT_PLAN', label: 'Behaviour Support Plan' },
  { value: 'MEDICAL', label: 'Medical' },
  { value: 'GUARDIAN_DOCUMENT', label: 'Guardian' },
  { value: 'RISK_ASSESSMENT_DOC', label: 'Risk Assessment' },
  { value: 'OTHER', label: 'Other' },
];

export function ParticipantDocumentsTab({ participantId }) {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const canManage = perms.includes('participants.manage_documents');
  const canUpload = perms.includes('participants.upload_documents') || canManage;
  const canDownload = perms.includes('participants.download_documents');
  const canPreview = perms.includes('participants.preview_documents');

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('ALL');
  const [showUpload, setShowUpload] = useState(false);

  const pageSize = 20;

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listParticipantDocuments(participantId, {
        page,
        pageSize,
        search: search || undefined,
        category: (category && category !== 'ALL') ? category : undefined,
      });
      setDocuments(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError('Failed to load documents');
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [participantId, page, search, category]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleArchive = async (docId) => {
    try {
      await archiveParticipantDocument(participantId, docId);
      toast.success('Document archived');
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive document');
    }
  };

  const handleUploadComplete = () => {
    setShowUpload(false);
    fetchDocuments();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchDocuments();
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4" data-testid="participant-documents-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Documents</h3>
        {canUpload && (
          <Button size="sm" onClick={() => setShowUpload(true)} data-testid="upload-document-btn">
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Upload Document
          </Button>
        )}
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
            data-testid="doc-search-input"
          />
        </div>
        <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
          <SelectTrigger className="w-[160px] h-8 text-sm" data-testid="doc-category-filter">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="button" variant="ghost" size="sm" onClick={fetchDocuments} className="h-8" data-testid="doc-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </form>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12" data-testid="doc-loading">
          <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading documents...</span>
        </div>
      ) : error ? (
        <div className="text-center py-12" data-testid="doc-error">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchDocuments} className="mt-3">Retry</Button>
        </div>
      ) : documents.length === 0 ? (
        <ParticipantDocumentEmptyState canManage={canUpload} onUpload={() => setShowUpload(true)} />
      ) : (
        <>
          <ParticipantDocumentList
            documents={documents}
            participantId={participantId}
            canManage={canManage}
            canDownload={canDownload}
            canPreview={canPreview}
            onArchive={handleArchive}
            onEdited={fetchDocuments}
          />
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2" data-testid="doc-pagination">
              <span className="text-xs text-muted-foreground">
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
              </span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <ParticipantDocumentUploadModal
          participantId={participantId}
          open={showUpload}
          onClose={() => setShowUpload(false)}
          onComplete={handleUploadComplete}
          canManage={canManage}
          existingDocuments={documents}
        />
      )}
    </div>
  );
}
